import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useSnackbar } from 'notistack'

import { useUpdateOrderMutation } from '@/app/services/orders'
import { selectContactId } from '@/app/slices/contactsSlice'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { ORDER_STATUS_ARRAY, ORDER_USER_TYPE } from '@/common/utils/Constants/Constants'

/**
 * Hook to automatically assign an order to the logged-in user when conditions are met
 * @param {Object} order - The current order object
 * @param {boolean} silent - Whether to show success/error notifications (default: true)
 * @returns {Object} - Object containing assignOrder function and loading state
 */
export const useAssignOrderToUser = (order, silent = true) => {
    const { t } = useTranslation()
    const { isAuthenticated } = useKindeAuth()
    const { enqueueSnackbar } = useSnackbar()

    const customerId = useSelector(selectCustomerId)
    const contactId = useSelector(selectContactId)

    const [updateOrder, { isLoading: isUpdatingOrder }] = useUpdateOrderMutation()

    // Change from useCallback to useMemo
    const isOrderAssignable = useMemo(() => {
        if (!order) return false

        const isEditable = [
            ORDER_STATUS_ARRAY.NotCalculated,
            ORDER_STATUS_ARRAY.Calculated,
            ORDER_STATUS_ARRAY.Draft,
        ].includes(order.status)
        const needsAssignment = !order.customerContactId && order.quoteUserType === ORDER_USER_TYPE.Guest
        const userAvailable = isAuthenticated && customerId && contactId

        return isEditable && needsAssignment && userAvailable
    }, [order, isAuthenticated, customerId, contactId])

    const assignOrder = useCallback(
        async (notificationSilent = silent) => {
            if (!isOrderAssignable) return false

            try {
                await updateOrder({
                    orderId: order.id,
                    organisationId: order.organisationId,
                    order: {
                        name: order.name,
                        quoteId: order.id,
                        customerContactId: contactId,
                        customerId: customerId,
                        quoteUserType: ORDER_USER_TYPE.Customer,
                    },
                }).unwrap()

                if (!notificationSilent) {
                    enqueueSnackbar(t('Order assigned to your account'), { variant: 'success' })
                }
                return true
            } catch (error) {
                console.error('Error assigning order to user:', error)
                if (!notificationSilent) {
                    enqueueSnackbar(t('Error assigning order to your account'), { variant: 'error' })
                }
                return false
            }
        },
        [order, customerId, contactId, isAuthenticated, updateOrder, enqueueSnackbar, t, isOrderAssignable, silent]
    )

    // Auto-assign order when component mounts if conditions are met
    useEffect(() => {
        if (isOrderAssignable) {
            assignOrder()
        }
    }, [order?.id, isOrderAssignable, assignOrder])

    return {
        assignOrder,
        isUpdatingOrder,
        isOrderAssignable,
    }
}
